import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    useMultiStyleConfig,
    Link as ChakraLink,
    Flex
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'

import useDetectDevice from '../../../hooks/use-detect-device'
import {useGlobalFooter} from '../../../contexts/global-elements-context'
import {parseShortcodes} from '../../optimizely/gdi/utils/gdi-shortcode-parser'
import {convertCmsStyles, mergeWithCmsSpacing} from '../utils'

const DownloadTheApp = ({...otherProps}) => {
    const styles = useMultiStyleConfig('Newsletter')
    const {isIOS, isSafari} = useDetectDevice()
    const {data: footerData} = useGlobalFooter()
    const goToApp = footerData?.goToApp

    if (!goToApp) return null

    const cmsTitleText = goToApp.title?.textForWeb ? parseShortcodes(goToApp.title.textForWeb) : null
    const cmsTitleStyles = convertCmsStyles(goToApp.title?.stylesPerViewType)
    const cmsSubtitleText = goToApp.subtitle?.textForWeb ? parseShortcodes(goToApp.subtitle.textForWeb) : null
    const cmsSubtitleStyles = convertCmsStyles(goToApp.subtitle?.stylesPerViewType)
    const cmsAndroidIconUrl = goToApp.androidIconUrl || null
    const cmsIosIconUrl = goToApp.iosIconUrl || null
    const cmsQrCodeUrl = goToApp.qrCodeUrl || null
    const cmsBackgroundColor = goToApp.backgroundColor || null

    const links = goToApp.links || []
    const appStoreLink = links.find(l => /apple\.com/i.test(l.LinkReference || ''))
    const playStoreLink = links.find(l => /play\.google\.com/i.test(l.LinkReference || ''))

    return (
        <Box
            {...styles.downloadAppContainer}
            {...(cmsBackgroundColor ? {bg: cmsBackgroundColor} : {})}
            {...otherProps}
        >
            <Flex direction="column" {...styles.downloadAppContent}>
                {cmsTitleText && (
                    <Box
                        {...mergeWithCmsSpacing(styles.downloadAppTitle, cmsTitleStyles)}
                        dangerouslySetInnerHTML={{__html: cmsTitleText}}
                    />
                )}
                {cmsSubtitleText && (
                    <Box
                        {...mergeWithCmsSpacing(styles.downloadAppSubTitle, cmsSubtitleStyles)}
                        dangerouslySetInnerHTML={{__html: cmsSubtitleText}}
                    />
                )}
            </Flex>

            <Flex {...styles.downloadAppStores}>
                {isIOS || isSafari ? (
                    <Flex>
                        {cmsQrCodeUrl && (
                            <HideOnMobile>
                                <Box as="img" src={cmsQrCodeUrl} alt="" aria-hidden="true" {...styles.logoQR} />
                            </HideOnMobile>
                        )}
                        {cmsIosIconUrl && (
                            <HideOnDesktop>
                                <Box>
                                    {appStoreLink?.LinkReference ? (
                                        <ChakraLink
                                            data-testid="footer-link-app-store"
                                            variant="plain"
                                            href={appStoreLink.LinkReference}
                                            isExternal
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            {...styles.xsTypo}
                                        >
                                            <Box as="img" src={cmsIosIconUrl} alt="Download on the App Store" {...styles.downloadAppLogo} />
                                        </ChakraLink>
                                    ) : (
                                        <Box as="img" src={cmsIosIconUrl} alt="Download on the App Store" {...styles.downloadAppLogo} />
                                    )}
                                </Box>
                            </HideOnDesktop>
                        )}
                    </Flex>
                ) : (
                    <Flex>
                        {cmsQrCodeUrl && (
                            <HideOnMobile>
                                <Box as="img" src={cmsQrCodeUrl} alt="" aria-hidden="true" {...styles.logoQR} />
                            </HideOnMobile>
                        )}
                        {cmsAndroidIconUrl && (
                            <HideOnDesktop>
                                <Box>
                                    {playStoreLink?.LinkReference ? (
                                        <ChakraLink
                                            data-testid="footer-link-play-store"
                                            variant="plain"
                                            href={playStoreLink.LinkReference}
                                            isExternal
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            {...styles.xsTypo}
                                        >
                                            <Box as="img" src={cmsAndroidIconUrl} alt="Get it on Google Play" {...styles.downloadPlayLogo} />
                                        </ChakraLink>
                                    ) : (
                                        <Box as="img" src={cmsAndroidIconUrl} alt="Get it on Google Play" {...styles.downloadPlayLogo} />
                                    )}
                                </Box>
                            </HideOnDesktop>
                        )}
                    </Flex>
                )}
            </Flex>
        </Box>
    )
}

DownloadTheApp.propTypes = {
    otherProps: PropTypes.object
}

export default DownloadTheApp
